import React, { ReactElement, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { FormControl, FormHelperText, Grid, GridSize, InputLabel } from '@material-ui/core';

import {
  CategoriesListController,
  PageTitle,
  SectionHeader,
  AddressSection,
  AvatarUpload,
  createMenuItems,
  CreateMenuItemsProps,
} from '@Components';
import { Button, TextInput, CheckboxTile, SelectInput } from '@Components/common';
import { useErrorStateStyles } from '@Components/common/themeOverrides';
import { msgFieldRequired } from '@Helpers/errorMessages';
import { useIsProfileComplete } from '@Helpers/useIsProfileComplete';
import { useGetCharityDetails } from '@Hooks/Api/useGetCharityDetails';
import { useCharityLookup } from '@Hooks/Api/useCharityLookup';
import { CharityLookupErrorEnum } from '@Types/CharityData';

import { SocialMediaFields } from './SocialMediaFields';
import styles from './Profile.module.scss';
import { CompanyAndCharityNumberFields } from '@Components/AccountPage/Organization/Components/CompanyAndCharityNumberFields';
import { CharityAddressDetails } from '@Components/AccountPage/Organization/Components/CharityAddressDetails';
import { OrganizationStructureEnum, FundingTypes, GroupTypes } from '@Types';

const DESCRIPTION_MAX_LENGTH = 1000;

export interface ProfileType {
  value: string;
  label: string;
}

interface ProfileProps {
  onSubmit: (e?: React.FormEvent<HTMLFormElement | HTMLButtonElement>) => Promise<void>;
  onCancel?: () => void;
  isSaving: boolean;
  isFunder: boolean;
  createMode: boolean;
  typeList: ProfileType[];
  headerDisclaimer: string;
  focusAreaDisclaimer: string | ReactElement;
}

export const Profile: React.FC<ProfileProps> = ({
  onSubmit,
  onCancel,
  isSaving,
  isFunder,
  createMode,
  typeList,
  headerDisclaimer,
  focusAreaDisclaimer,
}) => {
  const { t } = useTranslation('createAccount');
  const {
    register,
    control,
    errors: fieldErrors,
    watch,
    setValue,
    setError,
    clearErrors,
  } = useFormContext();
  const classes = useErrorStateStyles();
  const scrollRef = useRef<HTMLDivElement>(null);
  const { isProfileComplete } = useIsProfileComplete();
  const typeOfOrganization = watch('type');

  const charityNumber = watch('charity_number');
  const charityLookupResult = useCharityLookup(useGetCharityDetails, charityNumber);

  // Destructure frequently used charity data properties.
  const { charityData, errorType: charityErrorType } = charityLookupResult;

  // Handle form errors based on charity lookup result.
  useEffect(() => {
    if (!charityNumber || charityErrorType === CharityLookupErrorEnum.NONE) {
      clearErrors('charity_number');
      return;
    }

    let errorMessage = '';

    switch (charityErrorType) {
      case CharityLookupErrorEnum.INVALID_FORMAT:
        errorMessage = t('account:tabs.organization.registered_details.charity_number_regex_error');
        break;
      case CharityLookupErrorEnum.NOT_FOUND:
      case CharityLookupErrorEnum.API_ERROR:
        errorMessage = t('account:tabs.organization.charity_address_details.error_message');
        break;
    }

    if (errorMessage) {
      setError('charity_number', {
        type: 'manual',
        message: errorMessage,
      });
    }
  }, [charityErrorType, charityNumber, setError, clearErrors, t]);

  // Handler to populate the address fields in response to the user clicking the "Use this address" button.
  const handleUseAddress = useCallback(() => {
    if (charityData) {
      // Always replace ALL address fields to prevent stale data, even if charity data is empty for some fields.
      setValue('address1', (charityData.address_line_1 || '').trim() || '');
      setValue('address2', (charityData.address_line_2 || '').trim() || '');
      setValue('address3', (charityData.address_line_3 || '').trim() || '');
      setValue('city', (charityData.city || '').trim() || '');
      setValue('postcode', (charityData.postcode || '').trim() || '');
      // Reset county since charity data from API doesn't include county information.
      setValue('county', '');
    }
  }, [charityData, setValue]);

  // Show charity address details only if there is address data to display.
  const charityAddressProps = useMemo(() => {
    if (
      charityData &&
      (charityData.address_line_1.trim() ||
        charityData.address_line_2.trim() ||
        charityData.address_line_3.trim() ||
        charityData.city.trim() ||
        charityData.postcode.trim())
    ) {
      return {
        charityAddressData: charityData,
        onUseAddress: handleUseAddress,
      };
    }

    return null;
  }, [charityData, handleUseAddress]);

  const organizationStructureOptions: CreateMenuItemsProps['items'] = useMemo(
    () =>
      Object.keys(OrganizationStructureEnum).map((key) => ({
        label: t(`account:tabs.organization.registered_details.legal_structure_options.${key}`),
        menuItemProps: {
          value: key,
        },
      })),
    [t],
  );

  useEffect(() => {
    if (isProfileComplete && scrollRef?.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isProfileComplete]);

  return (
    <div>
      <form onSubmit={onSubmit} autoComplete="off">
        <div className={styles.contentContainer}>
          {createMode && (
            <>
              <PageTitle
                title={t('create_profile_common.header_create')}
                subtitle={headerDisclaimer}
              />

              <SectionHeader bigHeader title={t('create_profile_common.key_org_details')} />
            </>
          )}
          <TextInput
            label={t('create_profile_common.org_name')}
            inputRef={register({ required: msgFieldRequired })}
            name="name"
            error={!!fieldErrors.name}
            helperText={fieldErrors.name?.message}
            disabled={isSaving}
          />
          <SectionHeader title={t('create_profile_common.organization_avatar_header')} />
          <AvatarUpload required={createMode} />

          {!createMode && (
            <TextInput
              label={t('create_profile_common.description')}
              placeholder={t('create_profile_common.description_placeholder')}
              displayCharLimit={DESCRIPTION_MAX_LENGTH}
              inputRef={register({
                maxLength: DESCRIPTION_MAX_LENGTH,
              })}
              inputProps={{
                maxLength: DESCRIPTION_MAX_LENGTH,
              }}
              name="description"
              multiline
              minRows={2}
              maxRows={5}
              error={!!fieldErrors.description}
              helperText={fieldErrors.description?.message}
              disabled={isSaving}
            />
          )}

          <SectionHeader
            title={t('create_profile_common.org_title')}
            subtitle={!isFunder && t('create_profile_common.org_text')}
          />

          <FormControl
            classes={{ root: !!fieldErrors.type ? classes.errorState : classes.nonError }}
            error={!!fieldErrors.type}
            fullWidth
          >
            <Controller
              name="type"
              rules={{ required: msgFieldRequired }}
              control={control}
              defaultValue={''}
              render={({ onChange, value, ref }) => (
                <Grid container spacing={3}>
                  {typeList.map((type) => (
                    <Grid key={type.value} item xs={12} sm={(12 / typeList.length) as GridSize}>
                      <CheckboxTile
                        disabled={isSaving || (!createMode && !isFunder)}
                        checked={value.includes(type.value)}
                        error={!!fieldErrors.type}
                        value={type.value}
                        name="type"
                        label={type.label}
                        onChange={onChange}
                        inputRef={ref}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            />
            {fieldErrors.type?.message && (
              <FormHelperText>{fieldErrors.type?.message}</FormHelperText>
            )}
          </FormControl>

          {typeOfOrganization && (
            <CompanyAndCharityNumberFields typeOfOrganization={typeOfOrganization} />
          )}

          {!createMode && (
            <div className={styles.legalStructureContainer}>
              <FormControl error={!!fieldErrors.legal_structure}>
                <InputLabel>
                  {t('account:tabs.organization.registered_details.legal_structure')}
                </InputLabel>
                <Controller
                  name="legal_structure"
                  control={control}
                  rules={{ required: msgFieldRequired }}
                  defaultValue={''}
                  render={({ ref, onChange, value }) => (
                    <SelectInput
                      inputRef={ref}
                      onChange={onChange}
                      label={t('account:tabs.organization.registered_details.legal_structure')}
                      value={value || ''}
                      error={!!fieldErrors.legal_structure}
                    >
                      {createMenuItems({ items: organizationStructureOptions })}
                    </SelectInput>
                  )}
                />
                {fieldErrors.legal_structure?.message && (
                  <FormHelperText>{fieldErrors.legal_structure?.message}</FormHelperText>
                )}
              </FormControl>
            </div>
          )}
          <>
            <SectionHeader title={t('create_profile_common.phone_number_header')} />
            <TextInput
              inputRef={register({
                pattern: {
                  value: /^\+44\d{10}$/,
                  message: t('account:tabs.organization.registered_details.phone_number_error'),
                },
                required: msgFieldRequired,
              })}
              name="phone_number"
              label={t('account:tabs.organization.registered_details.phone_number')}
              placeholder="+441234567890"
              error={!!fieldErrors.phone_number}
              helperText={
                !fieldErrors.phone_number
                  ? t('create_profile_common.phone_number_hint')
                  : fieldErrors.phone_number?.message
              }
            />

            <SectionHeader
              title={t('create_profile_common.location')}
              subtitle={t('create_profile_common.add_primary_addr')}
            />
            {charityAddressProps && <CharityAddressDetails {...charityAddressProps} />}
            <AddressSection isLoading={isSaving} required />
          </>

          <SectionHeader
            title={t('create_profile_common.focus_areas')}
            subtitle={focusAreaDisclaimer}
          />

          <CategoriesListController name="categories" disabled={isSaving} required parentOnly />

          <>
            <SectionHeader title={t('create_profile_common.organization_set_up_year')} />
            <TextInput
              label={t('create_profile_common.organization_set_up_year')}
              type="number"
              inputRef={register({ valueAsNumber: true, required: msgFieldRequired })}
              name="set_up_year"
              error={!!fieldErrors.set_up_year}
              helperText={fieldErrors.set_up_year?.message}
              disabled={isSaving}
            />
          </>

          {!createMode && (
            <>
              <SectionHeader
                title={t('create_profile_common.online_presence')}
                subtitle={t('create_profile_common.online_presence_hint')}
              />
              <SocialMediaFields isSaving={isSaving} />
            </>
          )}

          <div className={styles.button}>
            {!createMode && (
              <Button onClick={onCancel} buttonType="tertiary">
                {t('common:cancel')}
              </Button>
            )}

            <Button onClick={onSubmit} buttonType="primary" disabled={isSaving}>
              {createMode ? t('common:create_profile') : t('common:save')}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};
